import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine initial language based on URL
  const getInitialLanguage = () => {
    if (location.pathname.startsWith('/en')) {
      return 'en';
    }
    return 'es';
  };

  const [language, setLanguage] = useState(getInitialLanguage());

  // Update language when URL changes
  useEffect(() => {
    const newLang = location.pathname.startsWith('/en') ? 'en' : 'es';
    if (newLang !== language) {
      setLanguage(newLang);
    }
  }, [location.pathname, language]);

  const toggleLanguage = () => {
    const currentPath = location.pathname;
    let newPath;

    // Mapping for legal pages that have different URL structures
    const esToEn = {
      '/aviso-legal': '/en/legal-notice',
      '/politica-de-privacidad': '/en/privacy-policy',
      '/politica-de-cookies': '/en/cookie-policy',
    };
    const enToEs = {
      '/en/legal-notice': '/aviso-legal',
      '/en/privacy-policy': '/politica-de-privacidad',
      '/en/cookie-policy': '/politica-de-cookies',
    };

    if (language === 'es') {
      // Switch to English
      if (esToEn[currentPath]) {
        newPath = esToEn[currentPath];
      } else if (currentPath === '/') {
        newPath = '/en';
      } else {
        newPath = '/en' + currentPath;
      }
    } else {
      // Switch to Spanish
      if (enToEs[currentPath]) {
        newPath = enToEs[currentPath];
      } else {
        newPath = currentPath.replace('/en', '') || '/';
      }
    }

    navigate(newPath);
    window.scrollTo(0, 0);
  };

  const value = {
    language,
    toggleLanguage,
    isEnglish: language === 'en',
    isSpanish: language === 'es'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
